import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { Lock, Target } from 'lucide-react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { PoliticalMatchQuestionStep } from '@/components/political-match/PoliticalMatchQuestionStep/PoliticalMatchQuestionStep';
import { usePoliticalMatchFlow } from '@/hooks/usePoliticalMatchFlow';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { buildPoliticalMatchSummary, formatPoliticalMatchScore } from '@/utils/political-match';

import styles from '@/styles/positions.module.css';

function clampCoordinate(score: number | null, axis: 'x' | 'y'): number {
  const raw = score === null ? 50 : axis === 'x' ? ((score + 2) / 4) * 100 : ((2 - score) / 4) * 100;

  return Math.max(10, Math.min(90, raw));
}

export function PositionsQuestionPage(): JSX.Element {
  const navigate = useNavigate();
  const {
    state,
    questionPlan,
    totalQuestions,
    currentQuestion,
    recordAnswer,
    advanceQuestion,
    goBackQuestion,
    setPhase,
    completeQuestionnaire,
    status,
    error,
  } = usePoliticalMatchFlow();

  const answeredQuestions = Object.values(state.answers).filter((answer) => answer !== null && answer !== undefined).length;
  const previewSummary = useMemo(() => buildPoliticalMatchSummary(questionPlan, state.answers), [questionPlan, state.answers]);
  const preview = useMemo(() => {
    const economicScore = previewSummary.economicScore;
    const personalScore = previewSummary.personalScore;
    const hasPosition = economicScore !== null || personalScore !== null;

    return {
      left: `${clampCoordinate(economicScore, 'x')}%`,
      top: `${clampCoordinate(personalScore, 'y')}%`,
      economicScore,
      personalScore,
      label: hasPosition ? 'Você' : 'Sem posição suficiente',
    };
  }, [previewSummary.economicScore, previewSummary.personalScore]);

  useSeo({
    title: buildPageTitle('Perguntas do Match Político'),
    description: 'Responda uma pergunta por vez para montar a comparação com os candidatos.',
    canonicalPath: '/match-politico/perguntas',
    indexable: false,
  });

  if (status === 'loading') {
    return <LoadingState title="Carregando perguntas" messages={['Preparando o questionário', 'Consultando suas respostas', 'Montando a próxima pergunta']} />;
  }

  if (status === 'error') {
    return <EmptyState tone="danger" title="Não foi possível carregar o questionário" description={error ?? ''} />;
  }

  if (!state.mode) {
    return <Navigate to="/match-politico" replace />;
  }

  if (!currentQuestion || totalQuestions === 0) {
    return <Navigate to="/match-politico/processando" replace />;
  }

  const selectedAnswer = state.answers[currentQuestion.id];
  const canGoNext = selectedAnswer !== undefined && selectedAnswer !== null;
  const isLastQuestion = state.currentQuestionIndex >= totalQuestions - 1;

  return (
    <section className={styles.questionScreen}>
      <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Perguntas' }]} />

      <StepHeader
        kicker="Etapa 2"
        title="Responda para comparar"
        description="Responda uma pergunta por vez para montar sua comparação."
        actions={
          <Button to="/match-politico/metodologia" variant="ghost" size="sm">
            Conheça a metodologia
          </Button>
        }
      />

      <div className={styles.questionSplitLayout}>
        <Card className={styles.questionLeadCard}>
          <PoliticalMatchQuestionStep
            question={currentQuestion}
            currentQuestionNumber={state.currentQuestionIndex + 1}
            totalQuestions={questionPlan.length}
            selectedAnswer={selectedAnswer}
            canGoNext={canGoNext}
            onSelectAnswer={(answer) => recordAnswer(currentQuestion.id, answer)}
            onBack={() => {
              if (state.currentQuestionIndex === 0) {
                setPhase('setup');
                navigate('/match-politico');
                return;
              }

              goBackQuestion();
            }}
            onNext={() => {
              if (!canGoNext) {
                return;
              }

              if (isLastQuestion) {
                completeQuestionnaire();
                setPhase('processing');
                navigate('/match-politico/processando');
                return;
              }

              advanceQuestion(totalQuestions);
            }}
            onSkip={() => {
              if (isLastQuestion) {
                completeQuestionnaire();
                setPhase('processing');
                navigate('/match-politico/processando');
                return;
              }

              advanceQuestion(totalQuestions);
            }}
          />
        </Card>

        <Card tone="soft" className={styles.questionPreviewCard}>
          <div className={styles.questionPreviewHeader}>
            <p className={styles.questionPreviewKicker}>Seu posicionamento (prévia)</p>
          </div>

          <div className={styles.questionPreviewMap} role="img" aria-label="Prévia do seu posicionamento no mapa">
            <span className={styles.questionPreviewAxisVertical} />
            <span className={styles.questionPreviewAxisHorizontal} />
            <span className={styles.questionPreviewOrigin} />
            <span className={styles.questionPreviewTopLabel}>Mais liberdade na vida pessoal</span>
            <span className={styles.questionPreviewRightLabel}>Mais liberdade no dinheiro e no trabalho</span>
            <span className={styles.questionPreviewBottomLabel}>Menos liberdade na vida pessoal</span>
            <span className={styles.questionPreviewLeftLabel}>Menos liberdade no dinheiro e no trabalho</span>

            <span className={styles.questionPreviewPoint} style={{ left: preview.left, top: preview.top }}>
              <span className={styles.questionPreviewPointPulse} />
              <span className={styles.questionPreviewPointDot}>
                <Target size={14} />
                </span>
              </span>
            </div>

            <div className={styles.questionPreviewMeta}>
            <Badge tone="neutral">
              {state.currentQuestionIndex + 1} de {totalQuestions} perguntas
            </Badge>
            <Badge tone="neutral">{answeredQuestions} respondidas</Badge>
            <Badge tone="primary">Dinheiro e trabalho: {formatPoliticalMatchScore(preview.economicScore)}</Badge>
            <Badge tone="primary">Vida pessoal: {formatPoliticalMatchScore(preview.personalScore)}</Badge>
          </div>

          <p className={styles.questionPreviewNote}>
            <Lock size={14} />
            {preview.label === 'Você'
              ? 'Esta é uma prévia baseada nas respostas já dadas. Complete o questionário para ver o posicionamento final.'
              : 'Complete o questionário para ver seu posicionamento no mapa.'}
          </p>
        </Card>
      </div>
    </section>
  );
}
